import { forgetCodeLabels, useCodeLabels } from "../codes";
import { useMemo, useState } from "react";
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  TabList,
} from "@fluentui/react-components";
import { addDays, CLOSED_STATUSES, DEFAULT_PROBABILITY, MAX_WEEKS, mondayOf, STATUS_LABEL, today, type ProjectStatus } from "../../../core";
import { errorText, send, useApi, type CodesResponse, type PeopleNamesResponse, type ProjectsResponse, type ProjectSummary } from "../api";
import { DateInput } from "../DateInput";
import { day, money, pct, when } from "../format";
import { go, href } from "../router";
import { ErrorBar, Loading, Page, useMe } from "../ui";
import { CopyDialog } from "./ProjectDialogs";
import { ProjectsDashboard } from "./ProjectsDashboard";

type Filter = "pipeline" | "booked" | "closed" | "templates" | "all";
type View = "dashboard" | Filter;

// The screen opens on the dashboard (PE15). Coming back from a project returns to the tab you left,
// for the rest of the browser session.
const TAB_KEY = "finance.projects.tab";
const VIEWS: readonly string[] = ["dashboard", "pipeline", "booked", "closed", "templates", "all"];
function rememberedView(): View {
  try {
    const v = sessionStorage.getItem(TAB_KEY);
    return v && VIEWS.includes(v) ? (v as View) : "dashboard";
  } catch {
    return "dashboard";
  }
}
function rememberView(v: View) {
  try {
    sessionStorage.setItem(TAB_KEY, v);
  } catch {
    // Storage can be blocked; the screen then opens on the dashboard each time.
  }
}

const FILTERS: { key: Filter; label: string; test: (p: ProjectSummary) => boolean }[] = [
  { key: "pipeline", label: "Pipeline", test: (p) => !p.isTemplate && ["prospect", "opportunity", "pending"].includes(p.status) },
  { key: "booked", label: "Booked", test: (p) => !p.isTemplate && p.status === "booked" },
  { key: "closed", label: "Closed", test: (p) => !p.isTemplate && CLOSED_STATUSES.has(p.status) },
  { key: "templates", label: "Templates", test: (p) => p.isTemplate },
  { key: "all", label: "All", test: () => true },
];

export function ProjectsList() {
  const { label: codeLabel } = useCodeLabels();
  const me = useMe();
  const data = useApi<ProjectsResponse>("/projects");
  const codes = useApi<CodesResponse>("/project-codes");
  const [view, setView] = useState<View>(rememberedView);
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();
  const editable = me.can("plans.edit");
  const financial = me.can("financials.read");
  const all = data.data?.projects ?? [];
  const rows = useMemo(() => {
    if (view === "dashboard") return [];
    const test = FILTERS.find((f) => f.key === view)!.test;
    const q = search.trim().toLowerCase();
    return all.filter((p) => test(p) && (!q || `${p.code} ${p.name} ${p.clientName}`.toLowerCase().includes(q)));
  }, [all, view, search]);

  return (
    <Page
      title="Projects"
      actions={
        editable && (
          <>
            <Button onClick={() => go("projects", "import")}>Import Allocation Plan…</Button>
            <Button appearance="primary" onClick={() => setCreating(true)}>
              New project
            </Button>
          </>
        )
      }
    >
      <ErrorBar message={error ?? data.error} />
      <div className="toolbar">
        <TabList
          selectedValue={view}
          onTabSelect={(_, d) => {
            setView(d.value as View);
            rememberView(d.value as View);
          }}
        >
          <Tab value="dashboard">Dashboard</Tab>
          {FILTERS.map((f) => (
            <Tab key={f.key} value={f.key}>
              {f.label} ({all.filter(f.test).length})
            </Tab>
          ))}
        </TabList>
        {view !== "dashboard" && <Input placeholder="Search code, name or client" value={search} onChange={(_, d) => setSearch(d.value)} aria-label="Search projects" />}
      </div>
      {view === "dashboard" ? (
        <ProjectsDashboard />
      ) : (
      <>
      <Caption1 className="muted">Each project's code is its own, and can be used on time cards and expenses as soon as the project is saved.</Caption1>
      {data.loading && !data.data ? (
        <Loading />
      ) : rows.length === 0 ? (
        <Body1 className="muted">{all.length === 0 ? "No projects yet." + (editable ? " Start with New project." : "") : "Nothing matches."}</Body1>
      ) : (
        <Table size="small" className="data-table" aria-label="Projects">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Code</TableHeaderCell>
              <TableHeaderCell>Project</TableHeaderCell>
              <TableHeaderCell>Status</TableHeaderCell>
              <TableHeaderCell>Start date</TableHeaderCell>
              <TableHeaderCell>End date</TableHeaderCell>
              <TableHeaderCell className="num">Hours</TableHeaderCell>
              {financial && <TableHeaderCell className="num">Price</TableHeaderCell>}
              {financial && <TableHeaderCell className="num">Cost</TableHeaderCell>}
              {financial && <TableHeaderCell className="num">Margin</TableHeaderCell>}
              <TableHeaderCell>Owner</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((p) => {
              const cost = (p.labor ?? 0) + (p.expenses ?? 0);
              const meets = p.contributionPct != null && p.targetMargin != null && p.contributionPct >= p.targetMargin - 1e-9;
              return (
                <TableRow key={p.id} className="clickable" onClick={() => go("projects", p.id)}>
                  <TableCell>
                    {p.isTemplate ? (
                      <Badge appearance="outline" size="small">
                        Template
                      </Badge>
                    ) : (
                      <>
                        <a href={href("projects", p.id)} onClick={(e) => e.stopPropagation()}>
                          {p.code ? codeLabel(p.code) : "—"}
                        </a>
                      </>
                    )}
                  </TableCell>
                  <TableCell>
                    {p.isTemplate ? <a href={href("projects", p.id)}>{p.name}</a> : p.name}
                    {p.clientName && <Caption1 className="block muted">{p.clientName}</Caption1>}
                  </TableCell>
                  <TableCell>
                    {!p.isTemplate && (
                      <>
                        {STATUS_LABEL[p.status]}
                        {!CLOSED_STATUSES.has(p.status) && p.status !== "booked" && <Caption1 className="muted"> {pct(p.probability ?? DEFAULT_PROBABILITY[p.status], 0)}</Caption1>}
                        {p.closedOn && <Caption1 className="block muted">{day(p.closedOn)}</Caption1>}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="nowrap">{day(p.startMonday)}</TableCell>
                  <TableCell className="nowrap">{day(p.end)}</TableCell>
                  <TableCell className="num">{Math.round(p.hours).toLocaleString("en-US")}</TableCell>
                  {financial && <TableCell className="num">{money(p.clientFees ?? 0)}</TableCell>}
                  {financial && <TableCell className="num">{money(cost)}</TableCell>}
                  {financial && (
                    <TableCell className="num">
                      {p.contributionPct == null ? (
                        "—"
                      ) : (
                        <Badge appearance="tint" color={meets ? "success" : "danger"} size="medium">
                          {pct(p.contributionPct)} {meets ? "✓" : "↓"}
                        </Badge>
                      )}
                    </TableCell>
                  )}
                  <TableCell>{p.ownerName ?? "—"}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
      </>
      )}
      {creating && <NewProjectDialog templates={all.filter((p) => p.isTemplate)} codes={codes.data?.codes ?? []} onClose={() => setCreating(false)} />}
    </Page>
  );
}

/** A blank plan, or one started from a template (PE11). Copying an existing project lives on that project. */
function NewProjectDialog(props: { templates: ProjectSummary[]; codes: CodesResponse["codes"]; onClose: () => void }) {
  const [from, setFrom] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [client, setClient] = useState("");
  const [status, setStatus] = useState<ProjectStatus>("prospect");
  const [start, setStart] = useState(mondayOf(addDays(today(), 7)));
  const [weeks, setWeeks] = useState("26");
  const [billable, setBillable] = useState(true);
  const [isTemplate, setIsTemplate] = useState(false);
  const [ownerId, setOwnerId] = useState("");
  const me = useMe();
  // Only a level that sees every project may make one for someone else (SEC2).
  const canAssign = me.scope === "all";
  const people = useApi<PeopleNamesResponse>(canAssign ? "/people?view=names" : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const template = useApi<{ project: ProjectSummary & { plan: { weeks: number; startMonday: string; assignments: unknown[]; schedule: unknown[]; expenses: unknown[] } } }>(from ? `/projects/${from}` : null);

  if (from && template.data) {
    const t = template.data.project;
    return <CopyDialog source={{ id: t.id, name: t.name, isTemplate: true, plan: t.plan as never, clientName: "", billable: t.billable }} codes={props.codes} onClose={props.onClose} onCreated={(id) => go("projects", id)} />;
  }

  const w = Math.round(Number(weeks));
  const valid = name.trim() && (isTemplate || code.trim()) && w >= 1 && w <= MAX_WEEKS;

  async function create() {
    setBusy(true);
    setError(undefined);
    try {
      forgetCodeLabels(); // the next screen reads the codes afresh, this one with them
      const { project } = await send<{ project: { id: string } }>("POST", "/projects", {
        project: { code: isTemplate ? "" : code, name, clientName: client, status, billable, isTemplate, ownerPersonId: ownerId || undefined },
        startMonday: mondayOf(start),
        weeks: w,
      });
      go("projects", project.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <Dialog open onOpenChange={(_, d) => !d.open && props.onClose()}>
      <DialogSurface className="dialog-wide">
        <DialogBody>
          <DialogTitle>New project</DialogTitle>
          <DialogContent className="dialog-stack">
            {props.templates.length > 0 && (
              <Field label="Start from" hint="A template brings its roles, hours, payment schedule and costs, moved to your start date.">
                <Select value={from} onChange={(_, d) => setFrom(d.value)}>
                  <option value="">A blank plan</option>
                  {props.templates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Select>
              </Field>
            )}
            {from && template.loading && <Loading label="Opening the template…" />}
            <div className="form-grid">
              {!isTemplate && (
                <Field label="Project code" required hint="Reported hours map to the project by this code. A code from a project closed before 2026 can't be reused.">
                  <Input
                    value={code}
                    onChange={(_, d) => {
                      const c = d.value.toUpperCase();
                      setCode(c);
                      const listed = props.codes.find((x) => x.code === c);
                      if (listed && !client) setClient(listed.client);
                    }}
                  />
                </Field>
              )}
              <Field label="Name" required>
                <Input value={name} onChange={(_, d) => setName(d.value)} />
              </Field>
              <Field label="Client">
                <Input value={client} onChange={(_, d) => setClient(d.value)} />
              </Field>
              {!isTemplate && (
                <Field label="Status">
                  <Select value={status} onChange={(_, d) => setStatus(d.value as ProjectStatus)}>
                    {(["prospect", "opportunity", "pending"] as const).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]} ({pct(DEFAULT_PROBABILITY[s], 0)})
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              {canAssign && (
                <Field label="Owner" hint="Change it later from the project's Details.">
                  <Select value={ownerId} onChange={(_, d) => setOwnerId(d.value)}>
                    <option value="">Me</option>
                    {(people.data?.people ?? [])
                      .filter((p) => p.status !== "archived")
                      .map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name}
                        </option>
                      ))}
                  </Select>
                </Field>
              )}
              <Field label="Starts" hint="Moves to the Monday of that week.">
                <DateInput type="date" value={start} onChange={(_, d) => setStart(d.value)} />
              </Field>
              <Field label="Weeks">
                <Input type="number" min={1} max={MAX_WEEKS} value={weeks} onChange={(_, d) => setWeeks(d.value)} />
              </Field>
            </div>
            <div className="checks">
              <Switch label="Billable" checked={billable} onChange={(_, d) => setBillable(d.checked)} />
              <Switch label="Make it a template" checked={isTemplate} onChange={(_, d) => setIsTemplate(d.checked)} />
            </div>
            <Caption1>New projects start as a prospect, opportunity or pending. Book one from its page, so booking takes Baseline 1.</Caption1>
          </DialogContent>
          <ErrorBar message={error} />
          <DialogActions>
            <Button onClick={props.onClose}>Cancel</Button>
            <Button appearance="primary" disabled={!valid || busy || !!from} onClick={() => void create()}>
              {busy ? "Creating…" : "Create"}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
